import {
  isRecord,
  type FormUiField,
  type FormUiFields,
} from '@elementary/common-ui/components/AiChat';
import { WAREHOUSE_DISPLAY_NAMES } from '@elementary/common-ui/constants/warehouses';
import { WarehouseType } from '@elementary/common-ui/types';
import {
  AthenaAuthMethod,
  BigQueryAuthMethod,
  DatabricksAuthMethod,
  DatabricksStorageAccessType,
  EnvironmentForm,
  SnowflakeAuthMethod,
} from 'shared/types';
import {
  CONNECTION_STEP_TITLES,
  DREMIO_HOST_OPTIONS,
  ENVIRONMENT_CONTENT_STEP_TITLE,
  ENVIRONMENT_DRAWER_ACTIONS,
  ENVIRONMENT_TYPE_OPTIONS,
} from '../constants';

export const ENVIRONMENT_FORM_TOOL_NAME = 'environment_form';

export const ENVIRONMENT_FORM_PROMPT = `The create/edit environment drawer on the Environments page, where users connect a data warehouse to Elementary (the onboarding flow). The first step ("${CONNECTION_STEP_TITLES.create}" or "${CONNECTION_STEP_TITLES.edit}") holds the environment name and type, the data warehouse and its credentials; the "${ENVIRONMENT_CONTENT_STEP_TITLE}" step holds the asset sources. Only the fields of the current step are visible. Page state: mode (create or edit), environmentId, step and steps (what each step means), hint (what the user does next), notes (setup guidance to share when relevant), submitError (the error from the last save attempt, such as a failed warehouse connection or a dbt project whose Elementary schema was not found), and on the ${ENVIRONMENT_CONTENT_STEP_TITLE} step savedAssetSourceIds and assetSourceRules for the selected warehouse. The tools never move between steps or save: the user clicks ${ENVIRONMENT_DRAWER_ACTIONS.continue}, ${ENVIRONMENT_DRAWER_ACTIONS.saveAndSync}, or ${ENVIRONMENT_DRAWER_ACTIONS.saveChanges}.`;

export const ENVIRONMENT_FORM_UNAVAILABLE_REASONS = {
  loading: 'The environment is still loading; try again in a moment',
  saving: 'The environment is being saved; try again after saving finishes',
};

export const ASSET_SOURCE_PREVIEW_LABEL = 'Asset source';

export const SSH_TUNNEL_NOTE =
  'SSH tunnel setup is done together with the Elementary team. If the user asks how to set up the tunnel or what to configure on their side, point them to Elementary support instead of guessing the steps.';

export const CONNECTION_STEP_DESCRIPTION =
  'Environment name and type, the data warehouse, and the credentials Elementary uses to connect to it.';

export const CONTENT_STEP_DESCRIPTION =
  'Asset sources Elementary monitors. A dbt project source reads the tables the Elementary dbt package writes (the Elementary schema, usually <schema>_elementary), so the package must already be installed and run. A warehouse source monitors tables directly, filtered by database and optional schemas (no schemas means all schemas in that database).';

const PORT_DESCRIPTION = 'An integer port number';

const RECOMMENDED_NOTE = 'Recommended';
const DEPRECATED_NOTE = 'Deprecated, not recommended';
const LEGACY_NOTE = 'Legacy';

const AWS_AUTH_LABELS = {
  secretAccessKey: 'Secret access key',
  roleArn: 'AWS role ARN',
};

const SECRET_FIELD = {
  secret: true,
  isSaved: (value: unknown) => value === true,
};

const CREDENTIAL_FILE_FIELD = {
  secret: true,
  isSaved: (value: unknown) =>
    value === true ||
    (isRecord(value) &&
      typeof value.content !== 'string' &&
      typeof value.name === 'string' &&
      value.name !== ''),
};

const showWhen =
  (field: string, value: unknown) => (values: Record<string, unknown>) =>
    values[field] === value;

export const ENVIRONMENT_DETAILS_FIELDS: FormUiFields<EnvironmentForm> = {
  environmentName: { label: 'Environment name', required: true },
  environmentType: {
    label: 'Environment type',
    options: ENVIRONMENT_TYPE_OPTIONS,
  },
};

export const WAREHOUSE_TYPE_FIELD: FormUiField<EnvironmentForm> = {
  label: 'Data warehouse',
  required: true,
  options: Object.entries(WAREHOUSE_DISPLAY_NAMES).map(([value, label]) => ({
    value,
    label,
  })),
};

export const SSH_TUNNEL_WAREHOUSE_TYPES: WarehouseType[] = [
  'redshift',
  'postgres',
];

export const SSH_TUNNEL_FIELDS: FormUiFields<EnvironmentForm> = {
  sshEnabled: { label: 'Connect via SSH tunnel' },
  sshHost: {
    label: 'SSH host',
    required: true,
    isVisible: showWhen('sshEnabled', true),
  },
  sshPort: {
    label: 'SSH port',
    description: PORT_DESCRIPTION,
    required: true,
    isVisible: showWhen('sshEnabled', true),
  },
  sshUsername: {
    label: 'SSH username',
    required: true,
    isVisible: showWhen('sshEnabled', true),
  },
};

export const WAREHOUSE_FIELDS: Record<
  WarehouseType,
  FormUiFields<EnvironmentForm>
> = {
  snowflake: {
    snowflakeAccount: {
      label: 'Account',
      description:
        'Snowflake account identifier, for example example.us-east-1',
      required: true,
    },
    snowflakeWarehouse: { label: 'Warehouse', required: true },
    snowflakeUserRole: { label: 'User role' },
    snowflakeAuthMethod: {
      label: 'Authentication method',
      options: [
        {
          value: SnowflakeAuthMethod.KEY_PAIR,
          label: 'Key pair',
          note: RECOMMENDED_NOTE,
        },
        {
          value: SnowflakeAuthMethod.PASSWORD,
          label: 'Username & password (deprecated)',
          note: DEPRECATED_NOTE,
        },
      ],
    },
    snowflakeUser: { label: 'User', required: true },
    snowflakePrivateKey: {
      ...SECRET_FIELD,
      label: 'Private key',
      required: true,
      isVisible: showWhen('snowflakeAuthMethod', SnowflakeAuthMethod.KEY_PAIR),
    },
    snowflakePrivateKeyPassphrase: {
      ...SECRET_FIELD,
      label: 'Private key passphrase',
      isVisible: showWhen('snowflakeAuthMethod', SnowflakeAuthMethod.KEY_PAIR),
    },
    snowflakePassword: {
      ...SECRET_FIELD,
      label: 'Password',
      required: true,
      isVisible: showWhen('snowflakeAuthMethod', SnowflakeAuthMethod.PASSWORD),
    },
  },
  redshift: {
    redshiftHost: { label: 'Host', required: true },
    redshiftPort: {
      label: 'Port',
      description: PORT_DESCRIPTION,
      required: true,
    },
    redshiftUsername: { label: 'User', required: true },
    redshiftPassword: { ...SECRET_FIELD, label: 'Password', required: true },
  },
  bigquery: {
    bigqueryLocation: {
      label: 'Location',
      description: 'BigQuery location, for example US',
      required: true,
    },
    bigqueryAuthMethod: {
      label: 'Authentication method',
      options: [
        { value: BigQueryAuthMethod.SERVICE_ACCOUNT, label: 'Service account' },
        {
          value: BigQueryAuthMethod.WIF,
          label: 'Workload Identity Federation',
        },
      ],
    },
    bigqueryAccountFile: {
      ...CREDENTIAL_FILE_FIELD,
      label: 'Service account file',
      required: true,
      isVisible: showWhen(
        'bigqueryAuthMethod',
        BigQueryAuthMethod.SERVICE_ACCOUNT,
      ),
    },
    bigqueryWifFile: {
      ...CREDENTIAL_FILE_FIELD,
      label: 'WIF credential file',
      required: true,
      isVisible: showWhen('bigqueryAuthMethod', BigQueryAuthMethod.WIF),
    },
  },
  databricks: {
    databricksHost: {
      label: 'Server host',
      description: 'For example 000-00000-0000.cloud.databricks.com',
      required: true,
    },
    databricksHttpPath: {
      label: 'HTTP path',
      description: 'For example /sql/1.0/endpoints/00000000',
      required: true,
    },
    databricksAuthMethod: {
      label: 'Authentication method',
      options: [
        {
          value: DatabricksAuthMethod.OAUTH,
          label: 'OAuth (M2M)',
          note: RECOMMENDED_NOTE,
        },
        {
          value: DatabricksAuthMethod.TOKEN,
          label: 'Access token (legacy)',
          note: LEGACY_NOTE,
        },
      ],
    },
    databricksClientId: {
      label: 'Client ID',
      required: true,
      isVisible: showWhen('databricksAuthMethod', DatabricksAuthMethod.OAUTH),
    },
    databricksClientSecret: {
      ...SECRET_FIELD,
      label: 'Client secret',
      required: true,
      isVisible: showWhen('databricksAuthMethod', DatabricksAuthMethod.OAUTH),
    },
    databricksAccessToken: {
      ...SECRET_FIELD,
      label: 'Access token',
      required: true,
      isVisible: showWhen('databricksAuthMethod', DatabricksAuthMethod.TOKEN),
    },
    databricksStorageAccessType: {
      label: 'Storage access method',
      options: [
        { value: null, label: 'None' },
        {
          value: DatabricksStorageAccessType.CREDENTIALS_VENDING,
          label: 'Credentials vending',
        },
        {
          value: DatabricksStorageAccessType.ACCESS_KEYS,
          label: `Direct storage access, ${AWS_AUTH_LABELS.secretAccessKey}`,
        },
        {
          value: DatabricksStorageAccessType.ROLE_ARN,
          label: `Direct storage access, ${AWS_AUTH_LABELS.roleArn}`,
        },
      ],
    },
    databricksStorageAccessKeyId: {
      ...SECRET_FIELD,
      label: 'Storage access key ID',
      required: true,
      isVisible: showWhen(
        'databricksStorageAccessType',
        DatabricksStorageAccessType.ACCESS_KEYS,
      ),
    },
    databricksStorageSecretAccessKey: {
      ...SECRET_FIELD,
      label: 'Storage secret access key',
      required: true,
      isVisible: showWhen(
        'databricksStorageAccessType',
        DatabricksStorageAccessType.ACCESS_KEYS,
      ),
    },
    databricksStorageSessionToken: {
      ...SECRET_FIELD,
      label: 'Storage session token',
      isVisible: showWhen(
        'databricksStorageAccessType',
        DatabricksStorageAccessType.ACCESS_KEYS,
      ),
    },
    databricksStorageRoleArn: {
      label: 'Storage AWS role ARN',
      required: true,
      isVisible: showWhen(
        'databricksStorageAccessType',
        DatabricksStorageAccessType.ROLE_ARN,
      ),
    },
    databricksStorageExternalId: {
      ...SECRET_FIELD,
      label: 'Storage external ID',
      isVisible: showWhen(
        'databricksStorageAccessType',
        DatabricksStorageAccessType.ROLE_ARN,
      ),
    },
  },
  postgres: {
    postgresHost: { label: 'Host', required: true },
    postgresPort: {
      label: 'Port',
      description: PORT_DESCRIPTION,
      required: true,
    },
    postgresUser: { label: 'User', required: true },
    postgresPassword: { ...SECRET_FIELD, label: 'Password', required: true },
  },
  athena: {
    athenaRegion: { label: 'Region', required: true },
    athenaS3StagingDir: {
      label: 'S3 staging directory',
      description:
        'Where Athena writes query results, for example s3://my-bucket/path/to/elementary-queries-output/',
      required: true,
    },
    athenaWorkgroup: { label: 'Workgroup' },
    athenaAuthMethod: {
      label: 'Authentication method',
      options: [
        {
          value: AthenaAuthMethod.SECRET_ACCESS_KEY,
          label: AWS_AUTH_LABELS.secretAccessKey,
        },
        { value: AthenaAuthMethod.ROLE_ARN, label: AWS_AUTH_LABELS.roleArn },
      ],
    },
    athenaAccessKeyId: {
      ...SECRET_FIELD,
      label: 'Access key ID',
      required: true,
      isVisible: showWhen(
        'athenaAuthMethod',
        AthenaAuthMethod.SECRET_ACCESS_KEY,
      ),
    },
    athenaSecretAccessKey: {
      ...SECRET_FIELD,
      label: 'Secret access key',
      required: true,
      isVisible: showWhen(
        'athenaAuthMethod',
        AthenaAuthMethod.SECRET_ACCESS_KEY,
      ),
    },
    athenaSessionToken: {
      ...SECRET_FIELD,
      label: 'Session token',
      isVisible: showWhen(
        'athenaAuthMethod',
        AthenaAuthMethod.SECRET_ACCESS_KEY,
      ),
    },
    athenaRoleArn: {
      label: 'AWS role ARN',
      required: true,
      isVisible: showWhen('athenaAuthMethod', AthenaAuthMethod.ROLE_ARN),
    },
    athenaExternalId: {
      ...SECRET_FIELD,
      label: 'External ID',
      isVisible: showWhen('athenaAuthMethod', AthenaAuthMethod.ROLE_ARN),
    },
  },
  clickhouse: {
    clickhouseHost: { label: 'Host', required: true },
    clickhousePort: {
      label: 'Port',
      description: PORT_DESCRIPTION,
      required: true,
    },
    clickhouseUser: { label: 'User', required: true },
    clickhousePassword: { ...SECRET_FIELD, label: 'Password', required: true },
  },
  dremio: {
    dremioHost: {
      label: 'Host',
      required: true,
      options: DREMIO_HOST_OPTIONS,
    },
    dremioProjectId: { label: 'Project ID', required: true },
    dremioUser: { label: 'User', required: true },
    dremioToken: { ...SECRET_FIELD, label: 'Token', required: true },
  },
  fabric: {
    fabricServer: {
      label: 'Server',
      description:
        'For example my-workspace.datawarehouse.fabric.microsoft.com',
      required: true,
    },
    fabricPort: {
      label: 'Port',
      description: PORT_DESCRIPTION,
      required: true,
    },
    fabricTenantId: { label: 'Tenant ID', required: true },
    fabricClientId: { label: 'Client ID', required: true },
    fabricClientSecret: {
      ...SECRET_FIELD,
      label: 'Client secret',
      required: true,
    },
  },
};

export const ASSET_SOURCES_FIELD: FormUiField<EnvironmentForm> = {
  label: 'Asset sources',
  description:
    'The full list of asset sources; a write replaces the current list. Each item is { type: "dbt", name, database, schema }, where schema is the Elementary schema the dbt package writes to, or { type: "warehouse", name, dbFilters: [{ database, schemas: [] }] }, where empty schemas means all schemas in that database. Keep the id of saved sources (savedAssetSourceIds) to keep them; leaving a saved source out removes it when the user saves. A saved source cannot change type, and a saved dbt source cannot change database or schema. Follow assetSourceRules, since some warehouses have no database or no schemas.',
  required: true,
};
